import {
  BulkInvitationColumnSpec,
  BulkInvitationFieldSpec,
  BulkInvitationRowValues,
  createEmptyRow,
  spreadsheetFields,
} from "legal-entities/invitations/bulk/bulk-invitation-column-spec";

/**
 * The only module that knows a spreadsheet library exists.
 *
 * SheetJS is imported dynamically at the moment somebody downloads a template or picks a file, so
 * it stays out of the main bundle - it is several times the size of the grid it serves, and most
 * people never open either.
 *
 * It is also the only place that parses something a user supplied. Everything it hands back is
 * plain rows of strings, validated afterwards by the same rules a typed row goes through, so a
 * sheet cannot introduce a value the grid would not have accepted from the keyboard.
 */

/** Cells a `boolean` column accepts as a tick, beyond a real boolean. */
const AFFIRMATIVE_CELLS = new Set(["yes", "y", "true", "t", "x", "1", "✓"]);

export class RosterSheetError extends Error {
  public readonly userFriendlyMessage: string;
  public readonly missingHeaders: readonly string[];

  public constructor(userFriendlyMessage: string, missingHeaders: readonly string[] = []) {
    super(userFriendlyMessage);
    this.name = "RosterSheetError";
    this.userFriendlyMessage = userFriendlyMessage;
    this.missingHeaders = missingHeaders;
  }
}

/**
 * Headings are matched on letters and digits alone, so a template that came back with different
 * capitalisation, a stray space or a trailing colon still lines up. A genuinely different word
 * does not, which is the point: that is a column nobody asked for.
 */
function normalizeHeader(value: unknown): string {
  return String(value ?? "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "");
}

function readCell(field: BulkInvitationFieldSpec, cell: unknown): string | boolean {
  if (field.kind === "boolean") {
    if (typeof cell === "boolean") return cell;
    return AFFIRMATIVE_CELLS.has(String(cell ?? "").trim().toLowerCase());
  }
  return String(cell ?? "").trim();
}

function isBlankRow(cells: readonly unknown[]): boolean {
  return cells.every((cell) => String(cell ?? "").trim().length === 0);
}

/**
 * Builds the blank sheet somebody fills in, from the same field list that draws the grid. Nothing
 * else generates it, so the template and the grid cannot describe different columns.
 */
export async function buildRosterTemplate(spec: BulkInvitationColumnSpec): Promise<Blob> {
  const XLSX = await import("xlsx");
  const fields = spreadsheetFields(spec);

  const sheet = XLSX.utils.aoa_to_sheet([fields.map((field) => field.header)]);
  // Wide enough to read the heading, which is the only content the template ships with.
  sheet["!cols"] = fields.map((field) => ({ wch: Math.max(16, field.header.length + 2) }));

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, sheet, spec.templateSheetName);

  const written = XLSX.write(workbook, { bookType: "xlsx", type: "array" }) as ArrayBuffer;
  return new Blob([written], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
}

export async function downloadRosterTemplate(spec: BulkInvitationColumnSpec): Promise<void> {
  const blob = await buildRosterTemplate(spec);
  const url = globalThis.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.setAttribute("href", url);
  link.setAttribute("download", `${spec.templateFileName}.xlsx`);
  link.click();
  globalThis.URL.revokeObjectURL(url);
}

/**
 * Reads a filled-in sheet into rows the grid can hold.
 *
 * A sheet that does not line up is reported by naming the heading that was looked for and not
 * found, never as a parse exception. A misspelled heading is indistinguishable from an absent one
 * - which is exactly why the message says what was expected rather than what was wrong.
 */
export async function parseRosterWorkbook(
  file: File | Blob,
  spec: BulkInvitationColumnSpec,
): Promise<readonly BulkInvitationRowValues[]> {
  const buffer = await file.arrayBuffer();

  const XLSX = await import("xlsx");
  let grid: unknown[][];
  try {
    const workbook = XLSX.read(buffer, { type: "array" });
    const sheetName = workbook.SheetNames[0];
    const sheet = sheetName ? workbook.Sheets[sheetName] : undefined;
    if (!sheet) throw new Error("no sheet");

    grid = XLSX.utils.sheet_to_json<unknown[]>(sheet, { header: 1, raw: false, defval: "" });
  } catch {
    throw new RosterSheetError(
      "This file could not be read as a spreadsheet. Download the template and fill that in.",
    );
  }

  const headerRow = grid.find((cells) => Array.isArray(cells) && !isBlankRow(cells));
  if (!headerRow) {
    throw new RosterSheetError("This sheet is empty. Download the template and fill that in.");
  }

  const columnOfHeader = new Map<string, number>();
  headerRow.forEach((cell, column) => {
    const normalized = normalizeHeader(cell);
    if (normalized.length > 0 && !columnOfHeader.has(normalized)) columnOfHeader.set(normalized, column);
  });

  const fields = spreadsheetFields(spec);
  const missing = fields
    .filter((field) => field.required && !columnOfHeader.has(normalizeHeader(field.header)))
    .map((field) => field.header);

  if (missing.length > 0) {
    throw new RosterSheetError(
      `This sheet is missing ${missing.length === 1 ? "a column" : "columns"}: ${missing.join(", ")}. ` +
        "Check the spelling of the headings, or download the template and fill that in.",
      missing,
    );
  }

  const bodyRows = grid.slice(grid.indexOf(headerRow) + 1);
  const rows: BulkInvitationRowValues[] = [];

  for (const cells of bodyRows) {
    if (!Array.isArray(cells) || isBlankRow(cells)) continue;

    const row = createEmptyRow(spec);
    for (const field of fields) {
      const column = columnOfHeader.get(normalizeHeader(field.header));
      if (column === undefined) continue;
      row[field.key] = readCell(field, cells[column]);
    }
    rows.push(row);
  }

  if (rows.length === 0) {
    throw new RosterSheetError("This sheet has headings but no rows under them.");
  }

  return rows;
}
